import React, { useState, useEffect } from 'react';
import HelpTooltip from '../../../../shared/components/HelpTooltip';
import {
  AlertTriangle, Shield, Globe, TrendingUp, TrendingDown,
  Activity, Users, Building2, Clock, Target, Zap,
  ChevronRight, Eye, Lock, Wifi, UserX
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import apiConfig from '../../../../config/apiConfig';
import { authHeaders } from '../../../../services/authHeaders';
import { resolveCurrentSessionId } from '../../../../services/currentSession';
import { getRiskBandLabel, getRiskBandStyle, getQuartileLabel, getRelativeComparison } from '../../../../utils/bands';

interface OutsidePressureData {
  overallScore: number;
  categories: {
    [key: string]: {
      description: string;
      factors: Array<{
        id: string;
        name: string;
        currentScore: number;
        impact: string;
        trend: string;
      }>;
    };
  };
  categoryScores: {
    [key: string]: {
      score: number;
      riskLevel: string;
    };
  };
  executiveSummary: {
    overallAssessment: string;
    highRiskAreas: string[];
    keyInsights: string[];
  };
  personalizationContext: {
    industry: string;
  };
}

interface PeerComparison {
  industry: string;
  ourScore: number;
  peerAverage: number;
  percentile: number;
  trend: 'improving' | 'declining' | 'stable';
}

interface ThreatEvolution {
  period: string;
  score: number;
  keyEvents: string[];
}

interface IndustryBenchmarkData {
  averageReadiness: number;
  averageRiskScore: number;
  averageRecoveryDays: number;
  revenueAtRiskPercent: number;
  recommendedSecurityPercent: number;
  investmentRange: { min: number; optimal: number; max: number };
}

interface Props {
  outsidePressureData?: any;
  industryBenchmark?: IndustryBenchmarkData;
}

const EnhancedThreatLandscape: React.FC<Props> = ({ outsidePressureData: passedOutsidePressureData, industryBenchmark }) => {
  const [fetchedData, setFetchedData] = useState<OutsidePressureData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');

  // Use passed data if available, otherwise use fetched data
  const outsidePressureData = passedOutsidePressureData || fetchedData;

  // Dynamic peer comparison based on actual data
  const [peerComparison, setPeerComparison] = useState<PeerComparison>({
    industry: 'Loading...',
    ourScore: 76,
    peerAverage: 68,
    percentile: 72,
    trend: 'improving'
  });

  // Update peer comparison from API-driven industry benchmark data
  useEffect(() => {
    const industryFromData = outsidePressureData?.personalizationContext?.industry;

    if (industryFromData) {
      const ourScore = outsidePressureData?.overallScore || 76;
      const peerAvg = industryBenchmark?.averageRiskScore
        ? (100 - industryBenchmark.averageRiskScore)
        : 68;
      const rawPercentile = peerAvg > 0
        ? Math.round(50 + ((peerAvg - ourScore) / peerAvg) * 50)
        : 50;
      const percentile = Math.max(5, Math.min(95, rawPercentile));
      const trend = ourScore <= peerAvg ? 'improving' as const : 'declining' as const;

      setPeerComparison({
        industry: industryFromData,
        ourScore,
        peerAverage: peerAvg,
        percentile,
        trend
      });
    }
  }, [outsidePressureData, industryBenchmark]);

  // Fetch outside pressure data (only if not passed as prop)
  useEffect(() => {
    if (passedOutsidePressureData) {
      setLoading(false);
      return;
    }

    const fetchOutsidePressureData = async () => {
      try {
        setLoading(true);
        const urlParams = new URLSearchParams(window.location.search);
        const assessmentId = urlParams.get('sessionId') || urlParams.get('assessmentId') || (await resolveCurrentSessionId());
        if (!assessmentId) return; // no session to analyse — finally clears loading
        const response = await fetch(`${apiConfig.getApiBaseUrl()}/risk-intelligence/outside-pressures/${assessmentId}`, { headers: authHeaders() });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.outsidePressures) {
            setFetchedData({
              ...result.outsidePressures,
              personalizationContext: result.personalizationContext
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch outside pressure data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutsidePressureData();
  }, [passedOutsidePressureData]);

  const getRiskLevelColor = (score: number) => {
    if (score >= 81) return 'text-red-600';
    if (score >= 61) return 'text-orange-600';
    return 'text-green-600';
  };

  const getRiskLevelBg = (score: number) => {
    if (score >= 81) return 'bg-red-100 border-red-200';
    if (score >= 61) return 'bg-orange-100 border-orange-200';
    return 'bg-green-100 border-green-200';
  };

  const getRiskLabel = (score: number): string => {
    if (score >= 81) return 'Critical';
    if (score >= 61) return 'Elevated';
    return 'Manageable';
  };

  const getRiskEmoji = (score: number): string => {
    if (score >= 81) return '🔴';
    if (score >= 61) return '🟡';
    return '🟢';
  };

  const getRiskDescription = (score: number): string => {
    if (score >= 81) return 'Immediate attention required';
    if (score >= 61) return 'Requires monitoring';
    return 'Within tolerance';
  };

  const getCategoryIcon = (categoryName: string) => {
    if (!categoryName || typeof categoryName !== 'string') {
      return <AlertTriangle aria-hidden="true" className="w-5 h-5" />;
    }
    const name = categoryName.toLowerCase();
    if (name.includes('geopolitical')) return <Globe aria-hidden="true" className="w-5 h-5" />;
    if (name.includes('regulatory')) return <Shield aria-hidden="true" className="w-5 h-5" />;
    if (name.includes('supply')) return <Building2 aria-hidden="true" className="w-5 h-5" />;
    if (name.includes('technology')) return <Zap aria-hidden="true" className="w-5 h-5" />;
    if (name.includes('economic')) return <TrendingUp aria-hidden="true" className="w-5 h-5" />;
    if (name.includes('competitive')) return <Target aria-hidden="true" className="w-5 h-5" />;
    if (name.includes('cyber')) return <Lock aria-hidden="true" className="w-5 h-5" />;
    return <AlertTriangle aria-hidden="true" className="w-5 h-5" />;
  };

  const getTrendIcon = (trend: string) => {
    const trendLower = trend.toLowerCase();
    if (trendLower.includes('increasing') || trendLower.includes('rapidly')) {
      return <TrendingUp aria-hidden="true" className="w-4 h-4 text-red-500" />;
    }
    if (trendLower.includes('decreasing') || trendLower.includes('declining')) {
      return <TrendingDown aria-hidden="true" className="w-4 h-4 text-green-500" />;
    }
    return <Activity aria-hidden="true" className="w-4 h-4 text-[#8892b0]" />;
  };

  if (loading) {
    return (
      <div className="ra-glass rounded-lg border border-[rgba(255,255,255,0.12)] p-6 h-full min-h-[600px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-[#a8b2d1]">Loading Enhanced Threat Landscape...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ra-glass rounded-lg border border-[rgba(255,255,255,0.12)] p-6 h-full">
      {/* Enhanced Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
            <Globe aria-hidden="true" className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#e6f1ff] flex items-center gap-1.5">Enhanced External Threat Landscape <HelpTooltip contentKey="compass.threatLandscape" /></h3>
            <p className="text-sm text-[#8892b0]">Real-time outside pressure analysis with industry intelligence</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            aria-label="Threat landscape time range"
            className="text-sm border border-[rgba(255,255,255,0.18)] rounded-lg px-3 py-1"
          >
            <option value="7d">7 Days</option>
            <option value="30d">30 Days</option>
            <option value="90d">90 Days</option>
          </select>
          
          {outsidePressureData && (() => {
            const overallBand = getRiskBandStyle(outsidePressureData.overallScore);
            return (
              <div className="px-4 py-2 rounded-lg border-2" style={{ backgroundColor: overallBand.bg, borderColor: overallBand.border }}>
                <div className="flex items-center space-x-2">
                  <span style={{ color: overallBand.text }}>{overallBand.icon}</span>
                  <span className="text-lg font-bold" style={{ color: overallBand.text }}>
                    {overallBand.label}
                  </span>
                  <span className="text-sm text-[#a8b2d1]">External Risk</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - Main Threat Categories */}
        <div className="xl:col-span-2 space-y-6">
          
          {/* External Threat Categories */}
          {outsidePressureData && (
            <div>
              <h4 className="text-lg font-semibold text-[#e6f1ff] mb-4 flex items-center">
                <Shield aria-hidden="true" className="w-5 h-5 mr-2 text-blue-600" />
                Critical External Risk Categories
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {outsidePressureData.categories && outsidePressureData.categoryScores && 
                 Object.entries(outsidePressureData.categoryScores)
                  .sort(([,a], [,b]) => (b?.score || 0) - (a?.score || 0))
                  .slice(0, 6)
                  .map(([categoryName, categoryScore]) => {
                    const categoryDetails = outsidePressureData.categories[categoryName];
                    return (
                      (() => {
                        const catBand = getRiskBandStyle(categoryScore?.score || 0);
                        return (
                          <div key={categoryName} className="rounded-lg border-2 p-4 transition-all hover:shadow-[0_8px_32px_rgba(0,217,255,0.12)]" style={{ backgroundColor: catBand.bg, borderColor: catBand.border }}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                {getCategoryIcon(categoryName)}
                                <div>
                                  <h5 className="font-semibold text-[#e6f1ff] text-sm">{categoryName}</h5>
                                  <div className="flex items-center mt-1">
                                    <span className="text-xl font-bold" style={{ color: catBand.text }}>
                                      {catBand.icon} {catBand.label}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <ChevronRight aria-hidden="true" className="w-4 h-4 text-[#5a6a8a]" />
                            </div>

                            {/* Top factors — trend + name only, no numeric score */}
                            <div className="space-y-2">
                              {categoryDetails?.factors && Array.isArray(categoryDetails.factors) && categoryDetails.factors.slice(0, 2).map((factor, idx) => (
                                <div key={idx} className="flex items-center text-xs space-x-1">
                                  {getTrendIcon(factor?.trend || 'Stable')}
                                  <span className="text-[#ccd6f6] truncate">{factor?.name || 'Unknown factor'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()
                    );
                  })}
              </div>
            </div>
          )}

          {/* Threat Evolution Bar Chart */}
          {outsidePressureData?.executiveSummary?.threatEvolution && (
            <div>
              <h4 className="text-lg font-semibold text-[#e6f1ff] mb-4 flex items-center">
                <Activity aria-hidden="true" className="w-5 h-5 mr-2 text-orange-600" />
                Threat Pressure Over Time
              </h4>
              <div className="ra-glass rounded-lg border border-[rgba(255,255,255,0.12)] p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={outsidePressureData.executiveSummary.threatEvolution} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.15)" />
                    <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#8892b0' }} stroke="rgba(255,255,255,0.2)" />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#8892b0' }} stroke="rgba(255,255,255,0.2)" />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{ background: '#172a45', border: '1px solid rgba(0, 217, 255, 0.25)', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', padding: '12px', fontSize: '0.875rem', zIndex: 50 }}>
                              <p className="font-semibold text-[#e6f1ff] mb-1">{data.period}</p>
                              <p className="text-[#ccd6f6]">Pressure Level: <span className="font-bold">{getRiskBandLabel(data.score)}</span></p>
                              {data.keyEvents && data.keyEvents.map((event: string, i: number) => (
                                <p key={i} className="text-xs text-[#8892b0] mt-1">- {event}</p>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="score" radius={[4, 4, 0, 0]} maxBarSize={60}>
                      {outsidePressureData.executiveSummary.threatEvolution.map((entry: any, index: number) => (
                        <Cell
                          key={index}
                          fill={entry.score >= 80 ? '#dc2626' : entry.score >= 60 ? '#ea580c' : '#f59e0b'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Industry Intelligence & Actions */}
        <div className="space-y-6">
          
          {/* Industry Peer Comparison */}
          <div>
            <h4 className="text-lg font-semibold text-[#e6f1ff] mb-4 flex items-center">
              <Users aria-hidden="true" className="w-5 h-5 mr-2 text-purple-600" />
              Industry Benchmark
            </h4>
            
            {(() => {
              const ourBand = getRiskBandStyle(peerComparison.ourScore);
              const peerBand = getRiskBandStyle(peerComparison.peerAverage);
              const relComp = getRelativeComparison(peerComparison.ourScore, peerComparison.peerAverage);
              const quartile = getQuartileLabel(peerComparison.percentile);
              return (
                <div className="ra-glass rounded-lg p-6">
                  <div className="text-center mb-4">
                    <div className="text-2xl font-bold text-purple-600 mb-1">{peerComparison.industry}</div>
                    <div className="text-sm text-[#a8b2d1]">Industry Sector</div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#a8b2d1]">Your Risk Level</span>
                      <span className="text-lg font-bold" style={{ color: ourBand.text }}>{ourBand.icon} {ourBand.label}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#a8b2d1]">Industry Average</span>
                      <span className="text-lg font-semibold" style={{ color: peerBand.text }}>{peerBand.label}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#a8b2d1]">Peer Position</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-[#ccd6f6]">{quartile}</span>
                        {peerComparison.trend === 'improving' ?
                          <TrendingUp aria-hidden="true" className="w-4 h-4 text-green-500" /> :
                          <TrendingDown aria-hidden="true" className="w-4 h-4 text-red-500" />
                        }
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.12)]">
                    <div className="text-xs text-[#a8b2d1] bg-[rgba(168,85,247,0.08)] rounded p-2">
                      Your external risk exposure is <strong>{relComp}</strong> {peerComparison.industry} industry peers.
                      {relComp.includes('above') && ' Focus on your highest-risk categories above.'}
                      {relComp.includes('below') && ' Continue monitoring emerging threats.'}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Executive Summary */}
          {outsidePressureData?.executiveSummary && (
            <div>
              <h4 className="text-lg font-semibold text-[#e6f1ff] mb-4 flex items-center">
                <Eye aria-hidden="true" className="w-5 h-5 mr-2 text-indigo-600" />
                Executive Intelligence
              </h4>
              
              <div className="ra-glass rounded-lg p-6">
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="font-semibold text-[#e6f1ff] mb-2">Overall Assessment:</div>
                    <p className="text-[#ccd6f6] leading-relaxed">{outsidePressureData.executiveSummary.overallAssessment}</p>
                  </div>
                  
                  {outsidePressureData.executiveSummary.highRiskAreas && outsidePressureData.executiveSummary.highRiskAreas.length > 0 && (
                    <div>
                      <div className="font-semibold text-[var(--ra-danger)] mb-2">High-Risk Areas:</div>
                      <ul className="space-y-1">
                        {outsidePressureData.executiveSummary.highRiskAreas.slice(0, 3).map((area, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <AlertTriangle aria-hidden="true" className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                            <span className="text-[#ccd6f6]">{area}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {outsidePressureData.executiveSummary.keyInsights && outsidePressureData.executiveSummary.keyInsights.length > 0 && (
                    <div>
                      <div className="font-semibold text-[var(--ra-cyber-aqua)] mb-2">Key Insights:</div>
                      <ul className="space-y-1">
                        {outsidePressureData.executiveSummary.keyInsights.slice(0, 2).map((insight, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <div className="w-1 h-1 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                            <span className="text-[#ccd6f6] text-xs">{insight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Immediate Actions - Dynamic from assessment data */}
          {outsidePressureData?.executiveSummary?.soWhatAnalysis?.specificRecommendations && (
            <div>
              <h4 className="text-lg font-semibold text-[#e6f1ff] mb-4 flex items-center">
                <Clock aria-hidden="true" className="w-5 h-5 mr-2 text-green-600" />
                Immediate Actions
              </h4>
              <p className="text-xs text-[#8892b0] mb-3 -mt-2">Based on industry threat patterns. Verify against your technical assessment for current control coverage.</p>

              <div className="ra-glass rounded-lg p-6">
                <div className="space-y-3 text-sm">
                  {outsidePressureData.executiveSummary.soWhatAnalysis.specificRecommendations.slice(0, 3).map((rec: any, idx: number) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}></div>
                      <div>
                        <div className="font-medium text-[#e6f1ff]">
                          {idx === 0 ? 'High Priority' : idx === 1 ? 'Medium Priority' : 'Low Priority'}
                        </div>
                        <div className="text-[#ccd6f6]">{rec.action}</div>
                      </div>
                    </div>
                  ))}
                  {/* Fallback if no recommendations */}
                  {(!outsidePressureData.executiveSummary.soWhatAnalysis.specificRecommendations || 
                    outsidePressureData.executiveSummary.soWhatAnalysis.specificRecommendations.length === 0) && (
                    <>
                      <div className="flex items-start space-x-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 mt-2 flex-shrink-0"></div>
                        <div>
                          <div className="font-medium text-[#e6f1ff]">High Priority</div>
                          <div className="text-[#ccd6f6]">Review supply chain risk assessments</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                        <div>
                          <div className="font-medium text-[#e6f1ff]">Medium Priority</div>
                          <div className="text-[#ccd6f6]">Update regulatory compliance monitoring</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0"></div>
                        <div>
                          <div className="font-medium text-[#e6f1ff]">Low Priority</div>
                          <div className="text-[#ccd6f6]">Enhance geopolitical risk tracking</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer with last update info */}
      <div className="mt-6 pt-4 border-t border-[rgba(255,255,255,0.12)] flex items-center justify-between text-sm text-[#8892b0]">
        <div>Last updated: Just now • Data source: Foundations Risk Intelligence</div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
          <span>Live data</span>
        </div>
      </div>
    </div>
  );
};

export default EnhancedThreatLandscape;